import type { ApiToken } from '../../model/api-tokens/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface ApiTokenUpdateApiOptions {
  tokenId: number
  name: string
  expiresAt?: string | null
  onStart?: () => void
  onSuccess?: (token: ApiToken) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

export const useApiTokenUpdateApi = async (
  options: ApiTokenUpdateApiOptions,
): Promise<ApiToken | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.api-tokens.update', {
    token_id: options.tokenId,
  })

  options.onStart?.()

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const { data } = await $axios.patch<ApiResponse>(url, {
      name: options.name,
      expires_at: options.expiresAt ?? null,
    })

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return null
    }

    const token: ApiToken = data.data.token
    toast.success('Token updated')
    options.onSuccess?.(token)
    return token
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)
    return null
  } finally {
    options.onFinish?.()
  }
}
